import { Outlet, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/client/components/theme-toggle'
import { appConfig } from '@/shared/config/app'

/**
 * Public layout for landing and auth pages
 * Provides simple header and footer for public pages
 *
 * ⚠️  SECURITY: Update VITE_APP_NAME and VITE_FOOTER_TEXT env vars
 * to rebrand for production (see src/shared/config/app.ts)
 */
export function PublicLayout() {
  const footerText = appConfig.footerText || `© ${new Date().getFullYear()} ${appConfig.name}. All rights reserved.`

  return (
    <div className="flex min-h-screen flex-col font-sans">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto max-w-7xl px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="bg-primary/10 p-1.5 rounded-lg group-hover:rotate-12 transition-transform">
                <span className="text-xl">🌿</span>
              </div>
              <span className="font-bold text-xl tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/70">
                {appConfig.name}
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">Generator</Link>
              <Link to="/races" className="hover:text-primary transition-colors">Races</Link>
              <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
              <Link to="/pricing" className="hover:text-primary transition-colors">Pricing</Link>
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <ThemeToggle />
            <div className="hidden sm:flex items-center gap-2">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/sign-in">Login</Link>
              </Button>
              <Button size="sm" className="rounded-full px-5 shadow-sm shadow-primary/20" asChild>
                <Link to="/sign-up">Join Pro</Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/40 py-12">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1 border-r border-border/50 pr-8">
              <div className="flex items-center space-x-2 mb-4">
                <span>🌿</span>
                <span className="font-bold">{appConfig.name}</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Empowering fantasy writers and roleplayers with unique elven names for over a decade.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">Generator</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">High Elf Names</Link></li>
                <li><Link to="/" className="hover:text-primary">Dark Elf Names</Link></li>
                <li><Link to="/" className="hover:text-primary">Wood Elf Names</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/about" className="hover:text-primary">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-primary">Contact</Link></li>
                <li><Link to="/blog" className="hover:text-primary">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/privacy" className="hover:text-primary">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-primary">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-border/50 text-muted-foreground text-xs gap-4">
            <p>{footerText}</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-primary transition-colors">Twitter</a>
              <a href="#" className="hover:text-primary transition-colors">Instagram</a>
              <a href="#" className="hover:text-primary transition-colors">Discord</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
